import axios from 'axios'
import { Toast } from 'vant'
import { Notify } from 'vant'

const rawBaseUrl = (window.publicConfig && window.publicConfig.BASE_API) || '/apiadmin/index.php?s=/apiadmin/'
const baseUrl = rawBaseUrl && String(rawBaseUrl).endsWith('/') ? rawBaseUrl : `${rawBaseUrl}/`
const defaultUploadUrl = (window.publicConfig && window.publicConfig.BASE_API) || '/apiadmin/index.php?s=/apiadmin/'
const storeId = (window.publicConfig && window.publicConfig.storeId) || 10001
const instance = axios.create({
  baseURL: baseUrl,
  timeout: 20000,
  headers: {
    'content-type': 'application/json;charset=utf-8'
  }
})

let requestNum = 0
const redirectToLogin = () => {
  return
}

const normalizeResponse = (response) => {
  const data = response && response.data
  if (data && typeof data === 'object' && Object.prototype.hasOwnProperty.call(data, 'status')) return data
  return { status: response.status, data }
}

const showRequestError = (message) => {
  Notify({ message: message || '网络请求出错', type: 'danger' })
}

instance.interceptors.request.use(
  (config) => {
    const nextConfig = { ...config }
    nextConfig.headers = nextConfig.headers || {}
    nextConfig.headers['storeid'] = storeId

    const token = localStorage.getItem('lxz-biz-token') || ''
    if (token) nextConfig.headers.AccessToken = token
    if (!token && !(nextConfig.meta && nextConfig.meta.skipAuth)) {
      redirectToLogin()
      return Promise.reject({ statusCode: 401, errMsg: '未登录', result: null })
    }

    if (nextConfig.meta && nextConfig.meta.load) {
      if (requestNum <= 0) Toast.loading({ message: '加载中', forbidClick: true, duration: 0 })
      requestNum += 1
    }

    return nextConfig
  },
  (error) => Promise.reject(error)
)

instance.interceptors.response.use(
  async (response) => {
    const meta = response && response.config ? response.config.meta : null
    if (meta && meta.load) {
      requestNum = requestNum - 1
      if (requestNum <= 0) Toast.clear()
    }

    const httpData = normalizeResponse(response)
    if (String(httpData.status) === '200') return httpData
    if (String(httpData.status) === '401') {
      localStorage.removeItem('lxz-biz-token')
      redirectToLogin()
      return Promise.reject({ statusCode: 401, errMsg: httpData.message || '未登录', result: httpData })
    }

    if (String(httpData.status) === '500') {
      if (!meta || meta.isPrompt !== false) showRequestError(httpData.message || '服务异常')
      return Promise.reject({ statusCode: 500, errMsg: httpData.message || '服务异常', result: httpData })
    }

    return Promise.reject({ statusCode: httpData.status, errMsg: httpData.message || '请求失败', result: httpData })
  },
  (error) => {
    const config = error && error.config ? error.config : null
    const meta = config ? config.meta : null
    if (meta && meta.load) {
      requestNum = requestNum - 1
      if (requestNum <= 0) Toast.clear()
    }

    const msg = (error && error.message) || ''
    if (!meta || meta.isPrompt !== false) showRequestError(msg)
    return Promise.reject({ statusCode: 0, errMsg: msg, result: null })
  }
)

const toSafeResponse = (err) => {
  if (err && typeof err === 'object' && Object.prototype.hasOwnProperty.call(err, 'status')) return err
  if (err && typeof err === 'object' && Object.prototype.hasOwnProperty.call(err, 'statusCode')) {
    return { status: err.statusCode, message: err.errMsg, data: err.result }
  }
  const message = (err && err.message) || '请求失败'
  return { status: 0, message, data: null }
}

const post = (url, data, options = {}) =>
  instance
    .post(url, data, { meta: { load: options.load, isPrompt: options.isPrompt, skipAuth: options.skipAuth } })
    .catch((err) => toSafeResponse(err))

const get = (url, params, options = {}) =>
  instance
    .get(url, { params, meta: { load: options.load, isPrompt: options.isPrompt, skipAuth: options.skipAuth } })
    .catch((err) => toSafeResponse(err))

const readFileAsDataURL = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result || ''))
    reader.onerror = reject
    reader.readAsDataURL(file)
  })

const loadImage = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = reject
    img.src = src
  })

const dataURLToBlob = (dataUrl) => {
  const parts = String(dataUrl).split(',')
  const mime = parts[0] && parts[0].match(/data:(.*?);base64/)
  const type = mime && mime[1] ? mime[1] : 'application/octet-stream'
  const binary = atob(parts[1] || '')
  const len = binary.length
  const bytes = new Uint8Array(len)
  for (let i = 0; i < len; i++) bytes[i] = binary.charCodeAt(i)
  return new Blob([bytes], { type })
}

const canvasToBlob = (canvas, type, quality) =>
  new Promise((resolve) => {
    if (canvas.toBlob) {
      canvas.toBlob((blob) => resolve(blob), type, quality)
      return
    }
    const dataUrl = canvas.toDataURL(type, quality)
    resolve(dataURLToBlob(dataUrl))
  })

const compressImageFile = async (file, options = {}) => {
  const type = file && file.type ? String(file.type) : ''
  if (!type.startsWith('image/')) return file
  if (type === 'image/gif') return file

  const maxWidth = options.maxWidth != null ? Number(options.maxWidth) : 1600
  const maxHeight = options.maxHeight != null ? Number(options.maxHeight) : 1600
  const quality = options.quality != null ? Number(options.quality) : 0.8

  const dataUrl = await readFileAsDataURL(file)
  const img = await loadImage(dataUrl)
  const originWidth = img.naturalWidth || img.width || 0
  const originHeight = img.naturalHeight || img.height || 0
  if (!originWidth || !originHeight) return file

  const ratio = Math.min(1, maxWidth / originWidth, maxHeight / originHeight)
  const targetWidth = Math.max(1, Math.round(originWidth * ratio))
  const targetHeight = Math.max(1, Math.round(originHeight * ratio))

  const canvas = document.createElement('canvas')
  canvas.width = targetWidth
  canvas.height = targetHeight
  const ctx = canvas.getContext('2d')
  if (!ctx) return file
  ctx.drawImage(img, 0, 0, targetWidth, targetHeight)

  const outType = type === 'image/png' ? 'image/png' : 'image/jpeg'
  const blob = await canvasToBlob(canvas, outType, quality)
  if (!blob) return file
  if (blob.size >= file.size) return file

  const ext = outType === 'image/png' ? 'png' : 'jpg'
  const name = file.name && file.name.indexOf('.') !== -1 ? file.name.replace(/\.[^.]+$/, `.${ext}`) : `image.${ext}`
  return new File([blob], name, { type: outType, lastModified: Date.now() })
}

const urlFileUpload = async ({ files, maxSize, url }) => {
  const list = Array.isArray(files) ? files : [files]
  const fileList = list
    .map((item) => (item && item.file ? item.file : item))
    .filter((item) => item instanceof File)

  if (!baseUrl) {
    return fileList.map((file) => ({
      data: {
        fileInfo: {
          file_id: `${Date.now()}_${Math.random().toString(16).slice(2)}`,
          preview_url: URL.createObjectURL(file)
        }
      }
    }))
  }

  const uploadUrl = url || defaultUploadUrl
  const results = []
  for (let i = 0; i < fileList.length; i++) {
    let nextFile = fileList[i]
    try {
      nextFile = await compressImageFile(nextFile, {
        maxWidth: (window.publicConfig && window.publicConfig.uploadMaxWidth) || 1600,
        maxHeight: (window.publicConfig && window.publicConfig.uploadMaxHeight) || 1600,
        quality: (window.publicConfig && window.publicConfig.uploadQuality) || 0.8
      })
    } catch (e) {
      nextFile = fileList[i]
    }

    if (maxSize && nextFile.size > maxSize) {
      Notify({ message: '图片过大，请重新上传', type: 'danger' })
      return Promise.reject({ statusCode: 0, errMsg: '图片过大' })
    }

    const formData = new FormData()
    formData.append('file', nextFile)
    console.log('uploadUrl', uploadUrl)
    const resp = await instance.post('/cleanerUpload/image', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      meta: { load: true, isPrompt: true }
    })
    const data = resp && resp.data ? resp.data : resp
    const fileInfo =
      (data && data.data && data.data.fileInfo) ||
      (data && data.fileInfo) || {
        file_id: '',
        preview_url: ''
      }
    results.push({ data: { fileInfo } })
  }
  return results
}

export default {
  get,
  post,
  urlFileUpload
}
